/**
 * This component will be used as a number input in the form design system
 * It will allow only number values (integer or float) and will validate the input based on the min and max props if provided
 */
import { Input } from "@design-system/ui/input";
import {
  maxRule,
  minRule,
  numberRule,
  requiredRule,
  useFormControl,
} from "@mongez/react-form";
import type { BaseInputProps } from "./BaseInput";
import { InputError } from "./InputError";
import { InputLabel } from "./InputLabel";

type NumberInputProps = BaseInputProps & {
  min?: number;
  max?: number;
  /**
   * Step value for increment/decrement
   * @default 1
   */
  step?: number;
};

export function NumberInput({
  step = 1,
  rightSide,
  leftSide,
  ...props
}: NumberInputProps) {
  const {
    changeValue,
    value = "",
    error,
    id,
    disabled,
    inputRef,
    otherProps,
  } = useFormControl({
    rules: [requiredRule, numberRule, minRule, maxRule],
    type: "number",
    ...props,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim();

    if (!newValue) {
      changeValue("");
      return;
    }

    // Allow only numbers, decimal points, and minus signs
    const numberRegex = /^-?\d*\.?\d*$/;
    if (!numberRegex.test(newValue)) {
      return;
    }

    // Prevent multiple decimal points
    const decimalCount = (newValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    // If the value ends with a decimal point, allow it for better UX
    if (newValue.endsWith(".") || newValue.endsWith("-")) {
      changeValue(newValue);
      return;
    }

    const numValue = Number(newValue);

    if (!isNaN(numValue)) {
      changeValue(numValue);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex flex-col gap-1">
        {props.label && (
          <InputLabel htmlFor={id} required={props.required}>
            {props.label}
          </InputLabel>
        )}
        {props.description && (
          <p className="text-sm text-muted-foreground">{props.description}</p>
        )}
      </div>
      <div className="relative">
        {leftSide && (
          <div className="absolute top-0 flex h-full items-center pl-3 pr-3 ltr:left-0 rtl:right-0">
            {leftSide}
          </div>
        )}
        <Input
          type="number"
          value={value}
          onChange={handleChange}
          id={id}
          ref={inputRef}
          disabled={disabled}
          step={step}
          className={`${leftSide ? "ltr:pl-10 rtl:pr-10" : ""}} ${rightSide ? "ltr:pr-10 rtl:pl-10" : ""}`}
          {...otherProps}
        />
        {rightSide && (
          <div
            className={`rtl:left-0" rtl:right-0"} absolute top-0 flex h-full items-center`}
          >
            {rightSide}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {props.hint && !error && (
          <p className="text-sm text-muted-foreground">{props.hint}</p>
        )}
        <InputError error={error} />
      </div>
    </div>
  );
}
